<template>
  <div
    :class="
      cn(variants({ size, disabled, hasError: !!errorMessage }), props.class)
    "
  >
    <label v-if="label">
      {{ label }}
      {{ required ? "*" : "" }}
    </label>
    <div class="flex grow">
      <div v-if="$slots.start" class="start">
        <slot name="start" />
      </div>
      <slot name="input">
        <Primitive
          ref="inputRef"
          :as="as"
          :type="type"
          :placeholder="placeholder"
          :value="modelValue"
          :rows="rows"
          :cols="cols"
          :disabled="disabled"
          :required="required"
          :autoFocus="autoFocus"
          :readonly="readonly"
          :class="
            cn(
              inputVariants({
                size,
                disabled,
                hasError: !!errorMessage,
                hasStartSlot: !!$slots.start,
                hasEndSlot: !!$slots.end,
                hasButton: !!$slots.button,
              }),
              props.class
            )
          "
          @input="onInputChange"
        />
      </slot>
      <div v-if="$slots.end" class="end">
        <slot name="end" />
      </div>
      <div v-if="$slots.button" :class="cn(buttonVariants({ size }))">
        <slot name="button" />
      </div>
    </div>
    <div v-if="errorMessage || hint" class="message">
      {{ errorMessage || hint }}
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { Primitive } from "radix-vue";
import {
  sizes,
  inputVariants,
  variants,
  buttonVariants,
} from "@/SharedComponents/Inputs";
import { cn } from "@/Utils/cn";
import { debounce } from "vue-debounce";

const inputRef = ref(null);

const emit = defineEmits(["click", "update:modelValue"]);

const props = defineProps({
  modelValue: { type: [String, Number], required: false },
  label: { type: String, required: false },
  as: { type: String, default: "input" },
  type: { type: String, default: "text" },
  placeholder: { type: String, default: "" },
  hint: { type: String, default: "" },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  autoFocus: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  size: {
    type: String,
    validator: (val) => sizes.includes(val),
    default: "md",
  },
  rows: { type: Number, required: false },
  cols: { type: Number, required: false },
  errorMessage: { type: String, default: "" },
  class: { type: null, required: false },
  debounceMs: { type: Number, default: 0 },
});

defineExpose({
  focus: () => inputRef.value?.$el.focus(),
});

const onInputChange = debounce((event) => {
  emit("update:modelValue", event.target.value);
}, props.debounceMs);
</script>
